/**
 * アバターの保有アイテム・装備の永続化(docs/02_game_design.md §7.1)。
 *
 * 本番のアイテム付与は Cloud Functions(`pullGacha`)経由になる(CLAUDE.md ルール2)。
 * ここは Phase 1 のローカル実装で、インターフェースは Firestore の
 * `inventories/{uid}/items/{itemId}` + `users/{uid}/avatar` にそのまま載せ替えられる形にしてある。
 */

import { ITEM_SLOTS, type ItemSlot } from './catalog';
import type { KeyValueStorage } from './progress';
import { appStorage, withoutUserStateNotify } from './userState';

/** ベースキャラのID(`logic/avatar.ts` の BASE_CHARACTERS と対応)。 */
export type BaseCharacterId = string;

export interface InventorySnapshot {
  readonly version: number;
  /** itemId → 所持数(重複はコイン変換されるので実質 1 だが、将来の重ね持ちに備える)。 */
  readonly items: Readonly<Record<string, number>>;
  readonly base: BaseCharacterId;
  /** スロット → 装備中のアイテムID。未装備のスロットはキーごと無い。 */
  readonly equipped: Readonly<Partial<Record<ItemSlot, string>>>;
}

export const DEFAULT_BASE_CHARACTER = 'base-01';

export const EMPTY_INVENTORY: InventorySnapshot = {
  version: 1,
  items: {},
  base: DEFAULT_BASE_CHARACTER,
  equipped: {},
};

const STORAGE_KEY = 'paltyping.inventory.v1';

/** 所持しているか。 */
export function owns(snapshot: InventorySnapshot, itemId: string): boolean {
  return (snapshot.items[itemId] ?? 0) > 0;
}

/** 所持アイテムIDの集合(ガチャの重複判定に使う)。 */
export function ownedIds(snapshot: InventorySnapshot): ReadonlySet<string> {
  return new Set(Object.keys(snapshot.items).filter((id) => (snapshot.items[id] ?? 0) > 0));
}

/**
 * アイテムを付与する(純関数)。
 *
 * 空きスロットのぶんは自動で装備する。小学生が「引いたのに見た目が変わらない」で
 * 迷わないようにするための配慮で、既に装備済みのスロットは触らない。
 */
export function applyGrant(
  snapshot: InventorySnapshot,
  itemIds: readonly string[],
  slotOf: (itemId: string) => ItemSlot | undefined,
): InventorySnapshot {
  if (itemIds.length === 0) return snapshot;
  const items = { ...snapshot.items };
  const equipped = { ...snapshot.equipped };
  for (const itemId of itemIds) {
    items[itemId] = (items[itemId] ?? 0) + 1;
    const slot = slotOf(itemId);
    if (slot !== undefined && equipped[slot] === undefined) equipped[slot] = itemId;
  }
  return { ...snapshot, items, equipped };
}

/** 装備を変える(純関数)。null で外す。未所持は無視する。 */
export function applyEquip(
  snapshot: InventorySnapshot,
  slot: ItemSlot,
  itemId: string | null,
): InventorySnapshot {
  const equipped = { ...snapshot.equipped };
  if (itemId === null) {
    delete equipped[slot];
  } else {
    if (!owns(snapshot, itemId)) return snapshot;
    equipped[slot] = itemId;
  }
  return { ...snapshot, equipped };
}

/**
 * 装備欄から**持っていないもの**を外す(純関数)。
 *
 * 「持っていない装備は無かったことにする」は保存値の読み直し
 * (`deserializeInventory`)と、サーバーからの読み戻し(`LocalInventoryService.hydrate`)の
 * **両方**で要る規則なので、ここに1つだけ置く(docs/04_data_model.md §5.1)。
 * 引数の `equipped` は保存値・受信値も来るので `unknown` で受ける。
 */
export function pruneEquipped(
  equipped: unknown,
  items: Readonly<Record<string, number>>,
): Partial<Record<ItemSlot, string>> {
  const out: Partial<Record<ItemSlot, string>> = {};
  if (typeof equipped !== 'object' || equipped === null) return out;
  for (const slot of ITEM_SLOTS) {
    const itemId = (equipped as Record<string, unknown>)[slot];
    if (typeof itemId === 'string' && (items[itemId] ?? 0) > 0) out[slot] = itemId;
  }
  return out;
}

/** 壊れたJSON・古いスキーマを捨てて既定値に落とす。 */
export function deserializeInventory(raw: string | null): InventorySnapshot {
  if (!raw) return EMPTY_INVENTORY;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return EMPTY_INVENTORY;
    const record = parsed as Partial<InventorySnapshot>;
    if (record.version !== 1) return EMPTY_INVENTORY;

    const items: Record<string, number> = {};
    if (typeof record.items === 'object' && record.items !== null) {
      for (const [id, count] of Object.entries(record.items)) {
        if (typeof count === 'number' && Number.isFinite(count) && count > 0) {
          items[id] = Math.floor(count);
        }
      }
    }

    // 持っていないものが装備欄に残っていたら無視する。
    const equipped = pruneEquipped(record.equipped, items);

    return {
      version: 1,
      items,
      base:
        typeof record.base === 'string' && record.base !== '' ? record.base : EMPTY_INVENTORY.base,
      equipped,
    };
  } catch {
    return EMPTY_INVENTORY;
  }
}

/** 保有・装備の読み書き。ローカル実装と Firebase 実装を差し替えられるようにする。 */
export interface InventoryService {
  load(): Promise<InventorySnapshot>;
  /** ガチャ結果などで新規アイテムを付与する。 */
  grant(
    itemIds: readonly string[],
    slotOf: (itemId: string) => ItemSlot | undefined,
  ): Promise<InventorySnapshot>;
  equip(slot: ItemSlot, itemId: string | null): Promise<InventorySnapshot>;
  setBase(base: BaseCharacterId): Promise<InventorySnapshot>;
  reset(): Promise<InventorySnapshot>;
}

/**
 * 読み戻しを受け取れる持ち物キャッシュ(docs/04_data_model.md §5.1)。
 *
 * サーバー権威なのは**持っているもの(`items`)だけ**。
 * 装備(`equipped`)とベースキャラ(`base`)は端末の見た目の設定なので触らない
 * —— ただし**読み戻した持ち物に無いアイテムだけは外す**
 * (持っていないものを着たままにしない。docs/04 §5.1 の規約)。
 */
export interface HydratableInventory {
  hydrate(
    items: Readonly<Record<string, number>>,
    options?: { readonly suppressSync?: boolean },
  ): Promise<InventorySnapshot>;
  /**
   * メモリ上のキャッシュを捨てて、次の `load()` で localStorage を読み直す。
   *
   * 読み戻し(docs/04 §5.1.1)は装備・ベースキャラを **localStorage 側**へ
   * 書き戻すので、そのあとこれを呼ばないと古い装備を持ち続けてしまう。
   */
  reload(): void;
}

/** localStorage 実装。Phase 2 で Firestore 実装に差し替える。 */
export class LocalInventoryService implements InventoryService, HydratableInventory {
  private snapshot: InventorySnapshot | null = null;

  constructor(
    private readonly storage: KeyValueStorage | null = appStorage(),
    private readonly key: string = STORAGE_KEY,
  ) {}

  /** 次の `load()` で localStorage を読み直す(読み戻しのあと)。 */
  reload(): void {
    this.snapshot = null;
  }

  async load(): Promise<InventorySnapshot> {
    this.snapshot ??= deserializeInventory(this.storage?.getItem(this.key) ?? null);
    return this.snapshot;
  }

  async grant(
    itemIds: readonly string[],
    slotOf: (itemId: string) => ItemSlot | undefined,
  ): Promise<InventorySnapshot> {
    const current = await this.load();
    return this.commit(applyGrant(current, itemIds, slotOf));
  }

  async equip(slot: ItemSlot, itemId: string | null): Promise<InventorySnapshot> {
    const current = await this.load();
    return this.commit(applyEquip(current, slot, itemId));
  }

  async setBase(base: BaseCharacterId): Promise<InventorySnapshot> {
    const current = await this.load();
    return this.commit({ ...current, base });
  }

  /**
   * サーバーの持ち物で `items` を上書きする(docs/04 §5.1 の読み戻し)。
   *
   * 装備・ベースキャラは端末のものなので**そのまま**残す。
   * 例外は「読み戻した持ち物に無いアイテム」で、そのスロットだけ外す
   * (別アカウントで入ったときに、持っていない帽子をかぶったままにしない)。
   */
  async hydrate(
    items: Readonly<Record<string, number>>,
    options: { readonly suppressSync?: boolean } = {},
  ): Promise<InventorySnapshot> {
    const current = await this.load();
    const next = { ...current, items, equipped: pruneEquipped(current.equipped, items) };
    return options.suppressSync
      ? withoutUserStateNotify(() => this.commit(next))
      : this.commit(next);
  }

  async reset(): Promise<InventorySnapshot> {
    this.snapshot = EMPTY_INVENTORY;
    this.storage?.removeItem(this.key);
    return this.snapshot;
  }

  private commit(next: InventorySnapshot): InventorySnapshot {
    this.snapshot = next;
    try {
      this.storage?.setItem(this.key, JSON.stringify(next));
    } catch {
      // 容量超過・プライベートモード等。保存できなくてもプレイは続行できる。
    }
    return next;
  }
}
